import React, { useState } from "react";
import { LuAlertCircle as AlertCircle, LuClock as Clock, LuCalendar as Calendar } from "react-icons/lu";
import { FileUploadInput, InputFile } from "src/components/Form/upload-input";
import { TextArea } from "src/components/Form/textarea";
import { CreatePostFormData } from "../../types/index.type";
import { useCreatePostMutation } from "../../post.service";
import { createPostSchema } from "../../utils/post.validation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Select } from "src/components/Form/select";
import { MultiSelect } from "src/components/Form/multi-select";
import { Controller } from "react-hook-form";
import { languageTuples } from "src/utils/util";
import { useAppDispatch } from "src/store";
import { showToast } from "src/store/toast.store";
import { postsLanguagesOptions } from "../../utils/buildPostQuery";

type Props = {
  previewMode: boolean;
};

export const CreatePostForm: React.FC<Props> = ({ previewMode }) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control,
  } = useForm<CreatePostFormData>({
    resolver: zodResolver(createPostSchema),
    defaultValues: {
      content: "",
      langCodes: [],
      scheduledDate: "",
    },
  });

  const [mediaFiles, setMediaFiles] = useState<Array<InputFile>>([]);
  const [isScheduled, setIsScheduled] = useState(false);
  const content = watch("content");
  const [createPost, { isSuccess, isLoading }] = useCreatePostMutation();

  const updateUploadedFile = (files: InputFile[]) => {
    setMediaFiles((prev) => [...prev, ...files]);
  };

  const handleScheduleToggle = () => {
    setIsScheduled(!isScheduled);
    if (!isScheduled) {
      // Clear the scheduled date when disabling schedule
      setValue("scheduledDate", "");
    }
  };

  const onSubmit = async (formData: CreatePostFormData) => {
    try {
      await createPost({
        ...formData,

        mediaFiles,
        scheduledDate: isScheduled ? formData.scheduledDate : undefined,
      }).unwrap();

      reset();
      setMediaFiles([]);
      setIsScheduled(false);

      const message = isScheduled ? "Post Scheduled Successfully" : "Post Published Successfully";
      dispatch(showToast({ type: "success", message }));
    } catch (error) {
      dispatch(showToast({ type: "error", message: error as string }));
    }
  };

  // Get current date-time in the correct format for datetime-local input
  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <TextArea previewMode={previewMode} registration={register("content")} value={content} label="content" />

      <Controller
        control={control}
        name="langCodes"
        render={({ field: { onChange, value } }) => (
          <MultiSelect
            options={languageTuples.map(([val, label]) => ({ value: val, label }))}
            label="Languages"
            value={value || []}
            onChange={onChange}
            error={errors.langCodes?.message}
          />
        )}
      />

      {/* Schedule Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleScheduleToggle}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${isScheduled
              ? "bg-blue-50 border-blue-300 text-blue-700"
              : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
          >
            <Clock size={16} />
            <span>{isScheduled ? "Scheduled" : "Schedule Post"}</span>
          </button>
        </div>

        {isScheduled && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Calendar size={16} className="inline mr-2" />
              Schedule Date & Time
            </label>
            <input
              type="datetime-local"
              min={getCurrentDateTime()}
              {...register("scheduledDate", {
                required: isScheduled ? "Please select a date and time" : false,
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.scheduledDate && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle size={16} />
                <span>{errors.scheduledDate.message}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-semibold text-gray-700">Media Upload</label>
        <FileUploadInput handleInputFile={updateUploadedFile} reset={isSuccess} />

        {errors.mediaFiles && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            <span>something went wrong when uploading the post images</span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading
            ? isScheduled
              ? "Scheduling..."
              : "Publishing..."
            : isScheduled
              ? "Schedule Post"
              : "Publish Now"}
        </button>
      </div>
    </form>
  );
};
